/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * EmptyStateCard
 *
 * @file EmptyStateCard.jsx
 * @author Mohamed yasar arabath M
 * @since 0.0.1
 */

import React from 'react';
import { Box, Typography, makeStyles, Button } from '@material-ui/core';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import mixins from '../../assets/styles/mixins.scss';

const useStyles = makeStyles(() => ({
  infoPanelRoot: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: mixins.chalkWhiteFont,
    borderRadius: 16,
    width: '100%'
  },
  infoPanelContent: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  infoPanelBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    alignItems: 'center'
  },
  iconWrapper: {
    background: mixins.badgeBgColor,
    borderRadius: 1000,
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  iconStyles: {
    width: 40,
    height: 40,
    color: mixins.amethystColor
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    alignItems: 'center'
  },
  titleText: {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  },
  descriptionText: {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize14,
    lineHeight: '22px'
  },
  subDescriptionText: {
    color: mixins.greyScaleColor,
    textAlign: 'center',
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize12,
    lineHeight: '20px'
  },
  courseCardDescriptionText: {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  },
  cancelButton: {
    borderRadius: '1000px',
    padding: 8,
    border: `2px solid ${mixins.color_text_primary}`,
    backgroundColor: 'transparent',
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    maxWidth: 80,
    height: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: mixins.color_text_primary
    }
  },
  retryButton: {
    borderRadius: '1000px',
    padding: 8,
    boxSizing: 'border-box',
    backgroundColor: mixins.amethystColor,
    color: mixins.chalkWhiteFont,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    textTransform: 'none',
    maxWidth: 107,
    height: 40,
    border: 'none',
    '&:hover': {
      backgroundColor: mixins.amethystColor,
      color: mixins.chalkWhiteFont
    }
  },
  inviteStudentsCTA: {
    borderRadius: '1000px',
    padding: '8px',
    border: `2px solid ${mixins.navyColor}`,
    backgroundColor: 'transparent',
    color: mixins.navyColor,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '24px',
    height: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: mixins.flaggedChipColor,
      color: mixins.navyColor
    }
  },
  actionWrapper: {
    display: 'flex',
    gap: 8
  },
  titleLeft: {
    alignSelf: 'flex-start',
    textAlign: 'left',
    width: '100%'
  }
}));


const EmptyStateCard = (props) => {
  const {
    iconName,
    title,
    description,
    subDescription,
    cancelCTA,
    retryCTA,
    showActions,
    inviteStudentsCTA,
    titleAlign = 'center'
  } = props;
  const classes = useStyles(props);
  const isTitleLeft = titleAlign === 'left';

  return (
    <Box className={classes.infoPanelRoot}>
      {isTitleLeft && title && (
        <Typography
          className={`${classes.titleText} ${isTitleLeft ? classes.titleLeft : ''}`}
        >
          {title}
        </Typography>
      )}
      <Box className={classes.infoPanelContent}>
        <Box className={classes.infoPanelBody}>
          <Box className={classes.iconWrapper}>
            <Icon name={iconName} className={classes.iconStyles} />
          </Box>
          <Box className={classes.textContainer}>
            {(!isTitleLeft && title) ? (
              <Typography className={classes.titleText}>
                {title}
              </Typography>
            ) : null}
            {description ?
              (
                <Typography className={!isTitleLeft ? classes.descriptionText : classes.courseCardDescriptionText}>
                  {description}
                </Typography>
              ) : null}

            {subDescription ?
              (
                <Typography className={classes.subDescriptionText}>
                  {subDescription}
                </Typography>
              ) : null}
          </Box>
          {showActions &&
            <div className={classes.actionWrapper}>
              {cancelCTA &&
              (
              <Button className={classes.cancelButton} onClick={cancelCTA.onClick}>
                {cancelCTA.text}
              </Button>
              )}

              {retryCTA &&
               (
               <Button className={classes.retryButton} onClick={retryCTA.onClick}>
                 {retryCTA.text}
               </Button>
               )}
              {inviteStudentsCTA &&
              (
              <Button className={classes.inviteStudentsCTA} onClick={inviteStudentsCTA.onClick}>
                {inviteStudentsCTA.text}
              </Button>
              )}
            </div>}
        </Box>
      </Box>
    </Box>
  );
};


EmptyStateCard.defaultProps = {
  iconName: 'CertificateIcon',
  description: '',
  subDescription: '',
  cancelCTA: null,
  retryCTA: null,
  showActions: false,
  inviteStudentsCTA: null,
  titleAlign: 'center'
};

EmptyStateCard.propTypes = {
  iconName: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  subDescription: PropTypes.string,
  cancelCTA: PropTypes.object,
  retryCTA: PropTypes.object,
  showActions: PropTypes.bool,
  inviteStudentsCTA: PropTypes.object,
  titleAlign: PropTypes.string
};

export default EmptyStateCard;
